import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Paper,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { PRODUCTS } from "../../utils/constants";
import { motion } from "framer-motion";

const QuoteForm = ({ onEmailRedirect }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Pre-select product if passed via navigation state
  const initialSelectedProduct = location.state?.selectedProduct || "";

  // Form validation schema
  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required(
        i18n.language === "tr" ? "Ad Soyad gereklidir" : "Full Name is required"
      )
      .min(
        3,
        i18n.language === "tr"
          ? "En az 3 karakter olmalıdır"
          : "Must be at least 3 characters"
      ),
    companyName: Yup.string().required(
      i18n.language === "tr"
        ? "Firma adı gereklidir"
        : "Company name is required"
    ),
    city: Yup.string().required(
      i18n.language === "tr" ? "Şehir gereklidir" : "City is required"
    ),
    country: Yup.string().required(
      i18n.language === "tr" ? "Ülke gereklidir" : "Country is required"
    ),
    phone: Yup.string().required(
      i18n.language === "tr" ? "Telefon gereklidir" : "Phone is required"
    ),
    email: Yup.string()
      .email(
        i18n.language === "tr"
          ? "Geçerli bir e-posta adresi giriniz"
          : "Enter a valid email address"
      )
      .required(
        i18n.language === "tr" ? "E-posta gereklidir" : "Email is required"
      ),
    product: Yup.string().required(
      i18n.language === "tr"
        ? "Ürün seçimi gereklidir"
        : "Product selection is required"
    ),
    quantity: Yup.number()
      .required(
        i18n.language === "tr" ? "Miktar gereklidir" : "Quantity is required"
      )
      .positive(
        i18n.language === "tr"
          ? "Miktar pozitif olmalıdır"
          : "Quantity must be positive"
      ),
    requestedDate: Yup.date()
      .nullable()
      .min(
        new Date(),
        i18n.language === "tr"
          ? "Geçmiş tarih seçilemez"
          : "Cannot select a past date"
      ),
  });

  // Initial form values
  const initialValues = {
    fullName: "",
    companyName: "",
    city: "",
    country: "",
    phone: "",
    email: "",
    website: "",
    product: initialSelectedProduct,
    quantity: "",
    requestedDate: "",
    detail: "",
  };

  // Form submission handler
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // Başarı mesajı yerine e-posta istemcisine yönlendir
    if (onEmailRedirect) {
      onEmailRedirect(values);
    }

    // Form sıfırlama ve gönderim durumunu güncelleme
    setSubmitting(false);
    resetForm();
  };

  // Animasyon varyantları
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <Card
          elevation={3}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: { xs: 2, md: 3 },
            bgcolor: "background.paper",
            overflow: "visible",
            boxShadow: {
              xs: "0 4px 8px rgba(0,0,0,0.1)",
              md: "0 8px 16px rgba(0,0,0,0.1)",
            },
            mb: 4,
          }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                component="h1"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: theme.palette.primary.main,
                }}
              >
                {t("form.quoteTitle", "Teklif Formu")}
              </Typography>
            </motion.div>

            <motion.div variants={itemVariants}>
              <Typography variant="body1" align="center" sx={{ mb: 4 }}>
                {t(
                  "form.quoteDescription",
                  "Aşağıdaki formu doldurarak ürünlerimiz hakkında teklif alabilirsiniz. En kısa sürede size dönüş yapacağız."
                )}
              </Typography>
            </motion.div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                touched,
                values,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form>
                  <Grid container spacing={{ xs: 2, md: 3 }}>
                    {/* Kişisel Bilgiler */}
                    <Grid item xs={12}>
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: 2,
                            p: 3,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            mb: 3,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              position: "relative",
                              textAlign: "center",
                              borderBottom: `2px solid ${theme.palette.secondary.main}`,
                              paddingBottom: 1,
                              fontSize: { xs: "1.1rem", md: "1.25rem" },
                            }}
                          >
                            {t("form.personalInfo", "Kişisel Bilgiler")}
                          </Typography>

                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="fullName"
                                name="fullName"
                                label={t("form.fullName", "Ad Soyad")}
                                value={values.fullName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.fullName && Boolean(errors.fullName)
                                }
                                helperText={touched.fullName && errors.fullName}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="companyName"
                                name="companyName"
                                label={t("form.companyName", "Firma Adı")}
                                value={values.companyName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.companyName &&
                                  Boolean(errors.companyName)
                                }
                                helperText={
                                  touched.companyName && errors.companyName
                                }
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="city"
                                name="city"
                                label={t("form.city", "Şehir")}
                                value={values.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.city && Boolean(errors.city)}
                                helperText={touched.city && errors.city}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="country"
                                name="country"
                                label={t("form.country", "Ülke")}
                                value={values.country}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.country && Boolean(errors.country)
                                }
                                helperText={touched.country && errors.country}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="phone"
                                name="phone"
                                label={t("form.phone", "Telefon")}
                                value={values.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.phone && Boolean(errors.phone)}
                                helperText={touched.phone && errors.phone}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="email"
                                name="email"
                                label={t("form.email", "E-posta")}
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                id="website"
                                name="website"
                                label={t("form.website", "Web Sitesi")}
                                value={values.website}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </motion.div>
                    </Grid>

                    {/* Ürün Bilgileri */}
                    <Grid item xs={12}>
                      <motion.div variants={itemVariants}>
                        <Box
                          sx={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: 2,
                            p: 3,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            mb: 2,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 3,
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              position: "relative",
                              textAlign: "center",
                              borderBottom: `2px solid ${theme.palette.secondary.main}`,
                              paddingBottom: 1,
                              fontSize: { xs: "1.1rem", md: "1.25rem" },
                            }}
                          >
                            {t("form.productInfo", "Ürün Bilgileri")}
                          </Typography>

                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <FormControl
                                fullWidth
                                error={
                                  touched.product && Boolean(errors.product)
                                }
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                    backgroundColor: theme.palette.grey[50],
                                    paddingRight: 1,
                                    paddingLeft: 1,
                                    transform:
                                      "translate(14px, -9px) scale(0.75)",
                                    "&.MuiInputLabel-shrink": {
                                      backgroundColor: theme.palette.grey[50],
                                      padding: "0 8px",
                                    },
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              >
                                <InputLabel id="product-label" shrink={true}>
                                  {t("form.product", "Ürün")}
                                </InputLabel>
                                <Select
                                  labelId="product-label"
                                  id="product"
                                  name="product"
                                  value={values.product}
                                  label={t("form.product", "Ürün")}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  displayEmpty
                                  renderValue={(selected) => {
                                    if (!selected) {
                                      return (
                                        <Typography
                                          color="text.secondary"
                                          style={{ opacity: 0.7 }}
                                        >
                                          {t(
                                            "form.selectProduct",
                                            "Ürün Seçiniz"
                                          )}
                                        </Typography>
                                      );
                                    }
                                    const selectedProduct = PRODUCTS.find(
                                      (p) => p.id === selected
                                    );
                                    return selectedProduct
                                      ? t(selectedProduct.nameKey)
                                      : selected;
                                  }}
                                  MenuProps={{
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                      },
                                    },
                                  }}
                                >
                                  {PRODUCTS.map((product) => (
                                    <MenuItem
                                      key={product.id}
                                      value={product.id}
                                    >
                                      {t(product.nameKey)}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {touched.product && errors.product && (
                                  <FormHelperText>
                                    {errors.product}
                                  </FormHelperText>
                                )}
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="quantity"
                                name="quantity"
                                label={t("form.quantity", "Miktar")}
                                type="number"
                                InputProps={{ inputProps: { min: 1 } }}
                                value={values.quantity}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.quantity && Boolean(errors.quantity)
                                }
                                helperText={touched.quantity && errors.quantity}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                id="requestedDate"
                                name="requestedDate"
                                label={t(
                                  "form.requestedDate",
                                  "Talep Edilen Tarih"
                                )}
                                type="date"
                                value={values.requestedDate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  touched.requestedDate &&
                                  Boolean(errors.requestedDate)
                                }
                                helperText={
                                  touched.requestedDate && errors.requestedDate
                                }
                                InputLabelProps={{
                                  shrink: true,
                                  style: {
                                    fontSize: "0.95rem",
                                    backgroundColor: theme.palette.grey[50],
                                    paddingRight: 5,
                                    paddingLeft: 5,
                                  },
                                }}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    height: { xs: "56px", md: "56px" },
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiFormHelperText-root": {
                                    marginLeft: 0,
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                id="detail"
                                name="detail"
                                label={t("form.detail", "Detay / Notlar")}
                                multiline
                                rows={isMobile ? 4 : 5}
                                value={values.detail}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                variant="outlined"
                                size={isMobile ? "medium" : "medium"}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 2,
                                    "&:hover fieldset": {
                                      borderColor: theme.palette.primary.light,
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "0.95rem",
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      </motion.div>
                    </Grid>

                    <Grid item xs={12} sx={{ mt: 3, textAlign: "center" }}>
                      <motion.div
                        variants={itemVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          color="secondary"
                          size={isMobile ? "large" : "large"}
                          disabled={isSubmitting}
                          sx={{
                            py: isMobile ? 1.2 : 1.5,
                            px: isMobile ? 4 : 5,
                            fontSize: isMobile ? "1rem" : "1.1rem",
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            background: `linear-gradient(45deg, ${theme.palette.secondary.main} 30%, ${theme.palette.secondary.light} 90%)`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          {t("form.send", "Gönder")}
                        </Button>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default QuoteForm;
